import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import type { LinkUplistingResponse } from '@natalan/capehost-contracts'
import { api } from '@/api/client'
import { useProperties } from '@/hooks/useApi'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { apiErrorMessage } from '@/lib/api-error'
import { describeLinkMove, readLinkConflicts } from '@/lib/onboarding'
import { pickableProperties } from '@/lib/property-lifecycle'

export interface LinkTarget {
  propertyId: string
  displayName: string
}

interface Candidate {
  id: string
  name: string
  uplistingId: string
}

function useCandidates() {
  const query = useProperties()
  const candidates = useMemo<Candidate[]>(
    () =>
      pickableProperties(query.data?.properties ?? [])
        .flatMap(p => {
          const id = String(p.property_id || p.id || '')
          // Live only (an onboarding property is local), with an Uplisting
          // id under either name admin-api accepts.
          const mappings = p.external_mappings ?? {}
          const uplistingId = mappings.uplisting_id ?? mappings.uplisting_property_id
          if (!id || !uplistingId) return []
          return [{ id, name: p.display_name || p.name || id, uplistingId: String(uplistingId) }]
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [query.data]
  )
  return { ...query, candidates }
}

interface LinkFlowProps {
  target: LinkTarget
  onDone: () => void
  busy: boolean
  setBusy: (busy: boolean) => void
}

function LinkFlow({ target, onDone, busy, setBusy }: LinkFlowProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { candidates, isLoading, isError } = useCandidates()
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState<Candidate | null>(null)
  // The dry run, with the listing it was run for: Link acts on that one only.
  const [plan, setPlan] = useState<{
    candidate: Candidate
    result: LinkUplistingResponse
  } | null>(null)
  const [error, setError] = useState<{ message: string; conflicts: string[] } | null>(null)

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q
      ? candidates.filter(c => `${c.name} ${c.id} ${c.uplistingId}`.toLowerCase().includes(q))
      : candidates
  }, [candidates, search])

  const run = async (dryRun: boolean) => {
    // The listing is fixed when the request starts: the dry run checks the
    // chosen one, and Link links exactly the one the dry run showed.
    const candidate = dryRun ? chosen : (plan?.candidate ?? null)
    if (!candidate || busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.linkUplisting(target.propertyId, candidate.id, dryRun)
      if (dryRun) {
        setPlan({ candidate, result })
      } else {
        void queryClient.invalidateQueries({ queryKey: ['properties'] })
        void queryClient.invalidateQueries({ queryKey: ['onboarding'] })
        void queryClient.invalidateQueries({ queryKey: ['property'] })
        // The profile, documents and tax registration now live under the
        // listing; whatever was cached for either id is out of date.
        for (const id of [target.propertyId, result.property_id]) {
          void queryClient.invalidateQueries({ queryKey: ['property-profile', id] })
          void queryClient.invalidateQueries({ queryKey: ['property-documents', id] })
          void queryClient.invalidateQueries({ queryKey: ['ma-tax-registration', id] })
        }
        toast({ title: 'Linked', description: `${target.displayName} is now ${candidate.name}.` })
        onDone()
        navigate(`/properties/${encodeURIComponent(result.property_id)}`)
      }
    } catch (err) {
      setError({
        message: apiErrorMessage(err, 'The link could not be made. Please try again.'),
        conflicts: readLinkConflicts((err as { body?: unknown } | null | undefined)?.body),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {!plan ? (
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textMuted" />
            <Input
              placeholder="Search Uplisting listings..."
              aria-label="Search Uplisting listings"
              disabled={busy}
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                // Never act on a listing the search has hidden.
                setChosen(null)
              }}
              className="pl-9"
            />
          </div>
          {isLoading ? (
            <LoadingSkeleton className="h-32 w-full" />
          ) : isError ? (
            <p className="text-sm text-danger">The listings could not be loaded.</p>
          ) : shown.length === 0 ? (
            <p className="text-sm text-textMuted">
              {search.trim()
                ? 'No listing matches.'
                : 'No Uplisting listing to link to yet. Sync Uplisting after the listing is created.'}
            </p>
          ) : (
            <ul className="max-h-64 space-y-1 overflow-y-auto" aria-label="Uplisting listings">
              {shown.map(candidate => (
                <li key={candidate.id}>
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-input px-2 hover:bg-surface2">
                    <input
                      type="radio"
                      name="link-target"
                      checked={chosen?.id === candidate.id}
                      disabled={busy}
                      onChange={() => {
                        setChosen(candidate)
                        setError(null)
                      }}
                    />
                    <span className="min-w-0">
                      <span className="block wrap-break-word text-sm text-text">
                        {candidate.name}
                      </span>
                      <span className="block font-mono text-xs text-textMuted">
                        Uplisting {candidate.uplistingId}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <p className="text-text">
            Linking to <span className="font-medium">{plan.candidate.name}</span> moves:
          </p>
          <ul className="max-h-64 space-y-1 overflow-y-auto" aria-label="What linking moves">
            {plan.result.moves.map(move => (
              <li
                key={`${move.from}->${move.to}`}
                className="rounded-input border border-border p-2"
              >
                <div className="text-text">{describeLinkMove(move)}</div>
                <div className="break-all font-mono text-xs text-textMuted">
                  {move.from === move.to ? move.from : `${move.from} -> ${move.to}`}
                </div>
              </li>
            ))}
          </ul>
          <p className="text-textMuted">
            Check this is the new owner&apos;s listing: the owner is given access to it alongside
            anyone who already has it. Afterwards the owner sees the full portal, and this page
            forwards to the listing.
          </p>
        </div>
      )}

      {error && (
        <div role="alert" className="space-y-1 text-sm text-danger">
          <p>{error.message}</p>
          {error.conflicts.length > 0 && (
            <ul className="list-inside list-disc break-all font-mono text-xs">
              {error.conflicts.map(c => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <DialogFooter className="gap-2">
        <Button
          variant="outline"
          onClick={
            plan
              ? () => {
                  setPlan(null)
                  setError(null)
                }
              : onDone
          }
          disabled={busy}
        >
          {plan ? 'Back' : 'Cancel'}
        </Button>
        {plan ? (
          <Button onClick={() => void run(false)} disabled={busy}>
            {busy ? 'Linking...' : 'Link'}
          </Button>
        ) : (
          <Button onClick={() => void run(true)} disabled={!chosen || busy}>
            {busy ? 'Checking...' : 'Check what moves'}
          </Button>
        )}
      </DialogFooter>
    </>
  )
}

/**
 * "Link to Uplisting listing" (CAP-1467): once the listing exists in
 * Uplisting and has synced, pick it, see what the link would move (a dry
 * run, nothing written), then link. admin-api refuses any move that would
 * overwrite something, and says which.
 */
export function LinkUplistingDialog({
  target,
  onClose,
}: {
  target: LinkTarget | null
  onClose: () => void
}) {
  // Not closable while a request is out: a link in flight still lands, and
  // would then toast and navigate from a dialog the operator thought shut.
  const [busy, setBusy] = useState(false)
  return (
    <Dialog open={target !== null} onOpenChange={open => !open && !busy && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Link to Uplisting listing</DialogTitle>
          <DialogDescription>
            Use this once {target?.displayName ?? 'the property'}&apos;s listing is in Uplisting.
            Its profile, documents, tax registration and owner access move to the listing.
          </DialogDescription>
        </DialogHeader>
        {target && (
          <LinkFlow
            key={target.propertyId}
            target={target}
            onDone={onClose}
            busy={busy}
            setBusy={setBusy}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
